import SymbolChartButton from '../SymbolChartButton';  // ADV-P0K
// [signal-decisions-v1] Trading UI primary panel: "Premarket Signal Decisions".
//
// This component is the FIRST thing visible at /signal. It renders the
// directional decision per row (bias / signal_use / trend / confidence /
// data_quality / reason / invalidation), driven by
// GET /api/v1/signal/decisions.
//
// The legacy SignalCandidateTable ("Ranked Signal Candidates") still
// exists below this panel, demoted to "Raw Ranking Inputs (secondary)"
// — see SignalCandidateTable.tsx for the rename.
//
// Hard rules (ADV-SIGNAL-SAFE-LABELS):
//   * Display labels are watch/review only — NO action-style labels. The backend
//     directional_decision signal_use is normalized via toSafeSignalUse() so a red
//     premarket mover renders as "Short Watch", never an action label.
//   * Stale / mismatched / unavailable data quality forces "Do Not Act"; low
//     confidence forces "Review Required" (fail-closed display precedence).
//   * We do not invent values. If the endpoint returns an empty rows list, we say so.

import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, AlertCircle, Activity } from "lucide-react";
import {
  getSignalDecisions,
  toSafeSignalUse,
  signalActionStatus,  // ADV-P0N
  type SignalDecisionsPayload,
  type SignalDecisionRow,
  type SignalBias,
} from "../../lib/signalDecisionsClient";

function biasClass(bias: SignalBias): string {
  if (bias === "Bullish") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  if (bias === "Bearish") return "bg-red-500/15 text-red-300 border-red-500/40";
  return "bg-slate-500/15 text-slate-300 border-slate-500/40";
}

// ADV-SIGNAL-SAFE-LABELS: tone keyed on the SAFE label key (never on an action label).
function safeUseClass(key: string): string {
  switch (key) {
    case "buy_candidate":  // ADV-P0N category
    case "watchlist_candidate": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
    case "short_candidate":  // ADV-P0N category
    case "short_watch": return "bg-red-500/15 text-red-300 border-red-500/40";
    case "monitor":
    case "review_required": return "bg-amber-500/15 text-amber-300 border-amber-500/40";
    case "high_risk": return "bg-orange-500/15 text-orange-300 border-orange-500/40";
    case "data_stale":
    case "do_not_act":
    default: return "bg-slate-500/15 text-slate-300 border-slate-500/40";
  }
}

function dataQualityClass(dq: string): string {
  if (dq === "live") return "text-emerald-400";
  if (dq === "after_hours") return "text-amber-400";
  if (dq === "delayed") return "text-amber-400";
  if (dq === "stale") return "text-red-400";
  return "text-red-500";
}

function fmtPct(v: number | null): string {
  if (v === null || v === undefined) return "—";
  return (v >= 0 ? "+" : "") + v.toFixed(2) + "%";
}

function fmtMoney(v: number | null): string {
  if (v === null || v === undefined) return "—";
  return "$" + v.toFixed(2);
}

function fmtAge(s: number | null | undefined): string {
  if (s === null || s === undefined) return "—";
  if (s < 60) return s + "s";
  if (s < 3600) return Math.round(s / 60) + "m";
  return Math.round(s / 3600) + "h";
}

export default function SignalDecisionTable() {
  const mounted = useRef(false);
  const [data, setData] = useState<SignalDecisionsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAvoid, setShowAvoid] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await getSignalDecisions();
      if (!mounted.current) return;
      setData(payload);
    } catch (e: any) {
      if (!mounted.current) return;
      setError(e?.message || "Failed to load signal decisions");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const summary = data?.summary;
  const rows = data?.rows || [];
  const visible = showAvoid
    ? rows
    : rows.filter(r => r.directional_decision.signal_use !== "Avoid");

  return (
    <section
      className="rounded-xl border border-[var(--app-border)] bg-[var(--app-panel)] mb-6"
      data-testid="signal-decision-table"
    >
      <header className="px-5 py-4 border-b border-[var(--app-border)] flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-bold uppercase tracking-wider text-[var(--app-text)] flex items-center gap-2">
            <Activity className="h-4 w-4 text-emerald-500" />
            Premarket Signal Decisions
          </h2>
          <p className="text-[11px] text-[var(--muted-text)] mt-1 max-w-3xl">
            Decision-first view. Every row carries Bias, Signal Use,
            Trend, Confidence, and Data Quality. Labels are watch/review
            only — red premarket movers render as Short Watch, never an
            action label. Stale or mismatched data forces Do Not Act.
            Source: <code>/api/v1/signal/decisions</code>.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--muted-text)]">
          <span>
            phase: {data?.market_phase ?? "—"} · age:{" "}
            {fmtAge(data?.candidate_source_age_seconds ?? null)}
          </span>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--app-border)] bg-[var(--app-bg)] px-2 py-1 text-[11px] hover:bg-[var(--app-panel)] disabled:opacity-50"
            data-testid="signal-decision-refresh"
          >
            <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
            Refresh
          </button>
          <button
            onClick={() => setShowAvoid(s => !s)}
            className="rounded-md border border-[var(--app-border)] bg-[var(--app-bg)] px-2 py-1 text-[11px] hover:bg-[var(--app-panel)]"
            data-testid="signal-decision-toggle-avoid"
          >
            {showAvoid ? "Hide Avoided" : "Show Avoided"}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-5 py-3 border-b border-[var(--app-border)]">
        <SummaryTile
          label="Bullish" value={summary?.bullish_candidates}
          tone="emerald" testid="summary-bullish"
        />
        <SummaryTile
          label="Bearish" value={summary?.bearish_candidates}
          tone="red" testid="summary-bearish"
        />
        <SummaryTile
          label="Reversal" value={summary?.reversal_watch}
          tone="amber" testid="summary-reversal"
        />
        <SummaryTile
          label="Avoid" value={summary?.avoid}
          tone="slate" testid="summary-avoid"
        />
        <SummaryTile
          label="Stale / Unavail" value={summary?.stale_or_unavailable}
          tone="red" testid="summary-stale"
        />
      </div>

      {error && (
        <div className="px-5 py-3 text-xs text-red-400 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="signal-decision-rows">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--muted-text)] border-b border-[var(--app-border)]">
              <th className="px-3 py-2">Rank</th>
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2 text-right">Premarket Move %</th>
              <th className="px-3 py-2">Bias</th>
              <th className="px-3 py-2">Signal Use</th>
              <th className="px-3 py-2">Trend</th>
              <th className="px-3 py-2">Confidence</th>
              <th className="px-3 py-2">Data Quality</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Invalidation</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && !loading && (
              <tr>
                <td colSpan={11} className="px-3 py-6 text-center text-[var(--muted-text)]">
                  {(rows.length === 0)
                    ? "No candidates returned by /api/v1/signal/decisions."
                    : "All current rows are Avoid. Click \"Show Avoided\" to see them."}
                </td>
              </tr>
            )}
            {visible.map(r => (
              <DecisionRow key={r.symbol} row={r} />
            ))}
          </tbody>
        </table>
      </div>

      <footer className="px-5 py-2 text-[10px] text-[var(--muted-text)] border-t border-[var(--app-border)] flex justify-between">
        <span>{rows.length} decisions · phase {data?.market_phase ?? "—"}</span>
        <span>Source: /api/v1/signal/decisions</span>
      </footer>
    </section>
  );
}

function SummaryTile({
  label, value, tone, testid,
}: { label: string; value: number | undefined; tone: string; testid: string }) {
  const colorClass = (() => {
    switch (tone) {
      case "emerald": return "text-emerald-300";
      case "red": return "text-red-300";
      case "amber": return "text-amber-300";
      default: return "text-slate-300";
    }
  })();
  return (
    <div
      className="rounded-lg border border-[var(--app-border)] bg-[var(--app-bg)] p-3"
      data-testid={testid}
    >
      <div className="text-[9px] uppercase tracking-widest text-[var(--muted-text)] font-bold">{label}</div>
      <div className={"text-2xl font-bold " + colorClass}>{value ?? "—"}</div>
    </div>
  );
}

function DecisionRow({ row }: { row: SignalDecisionRow }) {
  const d = row.directional_decision;
  return (
    <tr
      className="border-b border-[var(--app-border)]"
      data-testid={"signal-decision-row-" + row.symbol}
    >
      <td className="px-3 py-2 font-mono text-[var(--muted-text)]">{row.rank ?? "—"}</td>
      <td className="px-3 py-2 font-mono font-semibold"><span className="inline-flex items-center gap-1.5">{row.symbol}<SymbolChartButton symbol={row.symbol} /></span></td>
      <td className="px-3 py-2 text-right font-mono">{fmtMoney(row.price)}</td>
      <td className={"px-3 py-2 text-right font-mono " + ((row.premarket_gap_pct ?? 0) >= 0 ? "text-emerald-300" : "text-red-300")}>
        {fmtPct(row.premarket_gap_pct)}
      </td>
      <td className="px-3 py-2">
        <span
          className={"inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-widest " + biasClass(d.bias)}
          data-testid={"bias-" + row.symbol}
        >
          {d.bias}
        </span>
      </td>
      <td className="px-3 py-2">
        {(() => {
          // ADV-P0N: category label (may be "Buy Candidate"/"Short Candidate") on top,
          // separate action/safety status underneath. The category is never an order
          // instruction; the status states actionability + that nothing auto-trades.
          const safe = toSafeSignalUse(d);
          const act = signalActionStatus(d);
          return (
            <div className="flex flex-col gap-0.5">
              <span
                className={"inline-flex w-fit items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-widest " + safeUseClass(safe.key)}
                data-testid={"signal-use-" + row.symbol}
                data-signal-use={safe.label}
              >
                {safe.label}
              </span>
              <span
                className="text-[9px] font-semibold tracking-wide text-[var(--muted-text)]"
                data-testid={"action-status-" + row.symbol}
                data-action-status={act.label}
              >
                {act.label}
              </span>
            </div>
          );
        })()}
      </td>
      <td className="px-3 py-2 text-[var(--muted-text)]">{d.trend_state}</td>
      <td className="px-3 py-2 text-[var(--muted-text)]">{d.confidence}</td>
      <td className={"px-3 py-2 " + dataQualityClass(d.data_quality)}>{d.data_quality}</td>
      <td className="px-3 py-2 text-[var(--muted-text)] text-[11px] max-w-xs">{d.reason}</td>
      <td className="px-3 py-2 text-[var(--muted-text)] text-[11px] max-w-xs">{d.invalidation}</td>
    </tr>
  );
}
